import type { Objective } from '@/types/objectives';
import type { ObjectiveStatus } from '@/utils/objectiveStatus';

import { getObjectiveStatus } from '@/utils/objectiveStatus';

export interface ObjectivesSummary {
    total: number;
    achieved: number;
    onTrack: number;
    atRisk: number;
    overdue: number;
    /** Unweighted mean of the objectives' progressions, rounded; 0 when there is none. 100 only
     * once every objective is complete. */
    overall: number;
    /** `achieved / total` in percent, rounded; 0 when there is none. */
    completionRate: number;
}

/** Stands in for a figure while it is unknown -- never a zero, which would be a claim. */
export const UNKNOWN_FIGURE = '—';

/** The headline figures of a set of objectives. No objective reads as 0 %, not as an error. */
export const summarizeObjectives = (
    objectives: Pick<Objective, 'startDate' | 'endDate' | 'progression'>[],
): ObjectivesSummary => {
    const total = objectives.length;
    // Counted by the one rule every objective's card, page and filter read its state from, so a
    // count never disagrees with the objectives it counts.
    const counts: Record<ObjectiveStatus, number> = {
        completed: 0,
        onTrack: 0,
        atRisk: 0,
        overdue: 0,
    };

    const progressionSum = objectives.reduce((sum, objective) => {
        counts[getObjectiveStatus(objective)] += 1;

        return sum + objective.progression;
    }, 0);

    // 100 reads as "all done": an average rounded up to it (100 and 99 → 99.5) would claim it.
    const average = total ? Math.round(progressionSum / total) : 0;
    const overall = counts.completed === total ? average : Math.min(average, 99);

    return {
        total,
        achieved: counts.completed,
        onTrack: counts.onTrack,
        atRisk: counts.atRisk,
        overdue: counts.overdue,
        overall,
        completionRate: total ? Math.round((counts.completed / total) * 100) : 0,
    };
};
